import React, { useState } from 'react';
import DefaultModal from 'components/Utils/DefaultModal';
import PharmacySearchModal from 'components/Modals/PharmacySearchModal';
import PharmacyBadge from 'components/Pharmacy/PharmacyBadge';
import Icon from 'components/Utils/NewIcon';
import DefaultButton from 'components/Utils/NewDefaultButton';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import { useDefaultPharmacyState, useValidPreviousPharmacies } from 'hooks/useDefaultPharmacy';
import CPPUtils from 'services/CPPUtils';
import type { Patient } from 'generated/api';

const EMPTY: any[] = [];

interface PharmacySelectorProps {
  patient: Patient;
  /** The pharmacy this prescription will be faxed to. */
  pharmacy: any;
  /** Set the pharmacy for THIS prescription (does not touch the patient's default). */
  onSelect: (pharmacy: any) => void;
  /** Clear the pharmacy for this prescription. */
  onRemove: () => void;
}

/**
 * Pharmacy picker for a new prescription.
 *
 * Makes two previously-conflated concepts explicit:
 *  - which pharmacy THIS Rx is faxed to (per-prescription, `pharmacy`), and
 *  - the patient's saved default pharmacy (`default_pharmacy_id`).
 *
 * Choosing a pharmacy only affects this Rx. Updating the patient's default is a
 * deliberate, labelled action ("Save as default") so a one-off change can't
 * silently re-route every future prescription — the cause of wrong-place faxes.
 */
const PharmacySelector = ({ patient, pharmacy, onSelect, onRemove }: PharmacySelectorProps) => {
  const dispatch = useAppDispatch();
  const [searchOpen, setSearchOpen] = useState(false);
  const [savingDefault, setSavingDefault] = useState(false);
  // Reflect a freshly-saved default immediately, before the patient prop round-trips through redux.
  const [optimisticDefaultId, setOptimisticDefaultId] = useState<string | undefined>(undefined);

  const patientId = patient?.id;
  const prescriptions =
    useAppSelector((state) =>
      patientId ? state.healthRecords.byId[patientId]?.prescriptions : undefined,
    ) || EMPTY;

  const { pharmacy: defaultPharmacy } = useDefaultPharmacyState(patient);

  const effectiveDefaultId = optimisticDefaultId ?? defaultPharmacy?.id;
  const hasDefault = !!effectiveDefaultId;
  const hasSelection = !!pharmacy?.name;
  const isDefault = hasSelection && !!pharmacy?.id && pharmacy.id === effectiveDefaultId;
  const patientName = patient?.preferred_name || patient?.first_name || 'this patient';

  // Only offer recently-used pharmacies that still exist in the current
  // directory — a stale id won't bind to the patient or fax.
  const { pharmacies: previousPharmacies } = useValidPreviousPharmacies(
    prescriptions,
    defaultPharmacy,
  );

  function handleSelect(selected: any) {
    setSearchOpen(false);
    if (selected) onSelect(selected);
  }

  async function setAsDefault() {
    if (!pharmacy?.id || !patientId) return;
    setSavingDefault(true);
    const result = await dispatch(
      api.Patients.updatePatient({
        id: patientId,
        patient: { ...patient, default_pharmacy_id: pharmacy.id },
      }),
      {
        // `title` drives both the success and error toasts in the dispatch
        // wrapper — without it, success is silent (no feedback).
        title: 'Default Pharmacy',
        successText: `Saved as ${patientName}'s default pharmacy`,
        errorText: 'Could not update the default pharmacy',
      },
    );
    setSavingDefault(false);
    if (result) setOptimisticDefaultId(pharmacy.id);
  }

  const searchModal = searchOpen && (
    <DefaultModal
      key="pharmacy-search-modal"
      content={
        /*@ts-ignore*/
        <PharmacySearchModal
          onSelect={handleSelect}
          initialPharmacies={previousPharmacies}
          defaultPharmacyId={effectiveDefaultId}
          backButton={() => setSearchOpen(false)}
        />
      }
      isOpen={searchOpen}
      onRequestClose={() => setSearchOpen(false)}
    />
  );

  if (!hasSelection) {
    return (
      <>
        {searchModal}
        <div className="pharmacy-empty">
          <div className="pharmacy-status warn">
            <Icon src="triangle-exclamation" width="14px" />
            <span>
              No pharmacy selected — this prescription can’t be faxed until you choose one.
            </span>
          </div>
          <DefaultButton
            // @ts-ignore type is forwarded to the underlying button
            type="button"
            style={{ width: '200px' }}
            name="Select pharmacy"
            icon="magnifying-glass"
            className="white pharmacy-search-trigger"
            onClick={() => setSearchOpen(true)}
          />
        </div>
      </>
    );
  }

  return (
    <>
      {searchModal}
      <div className={`pharmacy-card ${isDefault ? 'is-default' : 'not-default'}`}>
        <div className="pharmacy-card-main">
          <div className={`pharmacy-status ${isDefault ? 'ok' : 'warn'}`}>
            <Icon src={isDefault ? 'circle-check' : 'triangle-exclamation'} width="14px" />
            <span>
              {isDefault
                ? `${patientName}'s default pharmacy`
                : hasDefault
                ? `Not ${patientName}'s default pharmacy — double-check before faxing`
                : `No default pharmacy saved for ${patientName} — double-check before faxing`}
            </span>
          </div>

          <PharmacyBadge pharmacy={pharmacy} />

          {!CPPUtils.getPharmacyFax(pharmacy) && (
            <div className="pharmacy-fax-confirm missing">
              <Icon src="triangle-exclamation" width="13px" />
              <span>No fax number on file — this prescription can’t be faxed.</span>
            </div>
          )}
        </div>

        <div className="pharmacy-card-actions">
          {!isDefault && (
            <DefaultButton
              // @ts-ignore type is forwarded to the underlying button
              type="button"
              name={hasDefault ? 'Make default' : 'Save as default'}
              icon="star"
              loading={savingDefault}
              className="white"
              onClick={setAsDefault}
            />
          )}
          <DefaultButton
            // @ts-ignore type is forwarded to the underlying button
            type="button"
            name="Change"
            icon="magnifying-glass"
            className="white pharmacy-search-trigger"
            onClick={() => setSearchOpen(true)}
          />
          <DefaultButton
            // @ts-ignore type is forwarded to the underlying button
            type="button"
            name="Remove"
            icon="trash"
            className="pharmacy-remove"
            onClick={onRemove}
          />
        </div>
      </div>
    </>
  );
};

export default PharmacySelector;
